import * as React from "react"
import { NetworkStatus, useQuery } from "@apollo/client"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import { useViewer } from "~/auth/use-viewer"
import { gql } from "~/__generated__"
import { ViewerOrganizationSwitcherQuery } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useToast } from "~/hooks/use-toast"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "~/ui/command"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "~/ui/dropdown-menu"
import { Skeleton } from "~/ui/skeleton"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "~/ui/sidebar"
import { cn } from "~/lib/utils"

const VIEWER_ORGANIZATION_SWITCHER_QUERY = gql(`
  query ViewerOrganizationSwitcher($search: String, $first: Int!, $after: String) {
    viewer {
      id
      currentOrganization {
        id
        name
        slug
        organizationType
        crossfadeSeconds
        billingEmail
        explicitTracksAllowed
      }
      recentOrganizations {
        id
        name
        slug
        organizationType
        crossfadeSeconds
        billingEmail
        explicitTracksAllowed
        stripeCustomerId
      }
      organizations(search: $search, first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            name
            slug
            organizationType
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

const SWITCH_ORGANIZATION_MUTATION = gql(`
  mutation SwitchOrganization($organizationId: ID!) {
    switchOrganization(input: { organizationId: $organizationId }) {
      user {
        id
        currentOrganization {
          id
          name
          slug
          organizationType
          crossfadeSeconds
          billingEmail
          explicitTracksAllowed
          stripeCustomerId
        }
        recentOrganizations {
          id
          name
          slug
          organizationType
        }
      }
    }
  }
`)

const PAGE_SIZE = 100

type ViewerData = NonNullable<ViewerOrganizationSwitcherQuery["viewer"]>
type OrganizationEdge = ViewerData["organizations"]["edges"][number]
type OrganizationNode = NonNullable<OrganizationEdge["node"]>
type OrganizationShape = {
  id: string
  name: string
  slug: string
  organizationType: OrganizationNode["organizationType"]
}

function toOrganizationShape(
  org: Pick<OrganizationShape, "id" | "name" | "slug" | "organizationType"> | null | undefined
): OrganizationShape | null {
  if (!org) return null
  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    organizationType: org.organizationType,
  }
}

function formatOrganizationType(type?: OrganizationNode["organizationType"]) {
  if (!type) return ""
  return type
    .toLowerCase()
    .split("_")
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

export function OrgSwitcher() {
  const { viewer } = useViewer()
  const { toast } = useToast()
  const { isMobile } = useSidebar()
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")
  const prevDebouncedSearchRef = React.useRef<string>("")

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [searchTerm])

  const { data, loading, fetchMore, refetch, networkStatus } = useQuery(
    VIEWER_ORGANIZATION_SWITCHER_QUERY,
    {
      variables: { search: debouncedSearch || null, first: PAGE_SIZE, after: null },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only", // Always fetch from network to ensure search works
    }
  )

  // Explicitly refetch when debounced search changes to ensure network request is made
  React.useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch
      refetch({ search: debouncedSearch || null, first: PAGE_SIZE, after: null })
    }
  }, [debouncedSearch, refetch])

  const [switchOrganization, { loading: switching }] = useSafeMutation(
    SWITCH_ORGANIZATION_MUTATION
  )

  const viewerData = data?.viewer ?? null

  const currentOrganization =
    toOrganizationShape(viewerData?.currentOrganization) ??
    toOrganizationShape(viewer.currentOrganization as OrganizationShape | null) ??
    null

  const rawRecentOrganizations =
    (viewerData?.recentOrganizations ?? viewer.recentOrganizations ?? []) as Array<
      Pick<OrganizationShape, "id" | "name" | "slug" | "organizationType">
    >

  const recentOrganizations: OrganizationShape[] = rawRecentOrganizations
    .map((org) => toOrganizationShape(org))
    .filter((org): org is OrganizationShape => org !== null)

  const organizationEdges: OrganizationEdge[] =
    viewerData?.organizations.edges.filter((edge): edge is OrganizationEdge & { node: OrganizationNode } => edge.node != null) ??
    []

  const connectionOrganizations: OrganizationShape[] = organizationEdges
    .map((edge) => toOrganizationShape(edge.node))
    .filter((org): org is OrganizationShape => org !== null)

  const hasNextPage = viewerData?.organizations.pageInfo.hasNextPage ?? false
  const endCursor = viewerData?.organizations.pageInfo.endCursor ?? null

  const knownOrganizations = React.useMemo(() => {
    const map = new Map<string, OrganizationShape>()
    for (const org of recentOrganizations) {
      map.set(org.id, org)
    }
    for (const org of connectionOrganizations) {
      map.set(org.id, org)
    }
    if (currentOrganization) {
      map.set(currentOrganization.id, currentOrganization)
    }
    return Array.from(map.values())
  }, [recentOrganizations, connectionOrganizations, currentOrganization])

  const [selectedOrganization, setSelectedOrganization] = React.useState<OrganizationShape | null>(
    currentOrganization
  )

  React.useEffect(() => {
    if (currentOrganization) {
      setSelectedOrganization(currentOrganization)
    }
  }, [currentOrganization?.id])

  const handleLoadMore = React.useCallback(() => {
    if (!hasNextPage || !endCursor) return
    fetchMore({
      variables: { after: endCursor, first: PAGE_SIZE, search: debouncedSearch || null },
      updateQuery(previousResult, { fetchMoreResult }) {
        if (!fetchMoreResult?.viewer || !previousResult.viewer) {
          return previousResult
        }

        return {
          __typename: previousResult.__typename ?? "Query",
          viewer: {
            ...fetchMoreResult.viewer,
            organizations: {
              ...fetchMoreResult.viewer.organizations,
              edges: [
                ...previousResult.viewer.organizations.edges,
                ...fetchMoreResult.viewer.organizations.edges,
              ],
            },
          },
        }
      },
    })
  }, [fetchMore, hasNextPage, endCursor, debouncedSearch])

  const handleSelect = React.useCallback(
    async (organizationId: string) => {
      if (switching || selectedOrganization?.id === organizationId) {
        setOpen(false)
        return
      }

      const targetOrganization = knownOrganizations.find((org) => org.id === organizationId) ?? null
      if (targetOrganization) {
        setSelectedOrganization(targetOrganization)
      }

      try {
        await switchOrganization({ variables: { organizationId } })
        await refetch()
      } catch (error) {
        console.error("Failed to switch organization", error)
        toast({
          title: "Unable to switch organization",
          description: "Please try again.",
          variant: "destructive",
        })
        await refetch()
      } finally {
        setOpen(false)
      }
    },
    [switchOrganization, selectedOrganization?.id, knownOrganizations, refetch, toast, switching]
  )

  const isSearching = networkStatus === NetworkStatus.refetch

  const filteredOrganizations = React.useMemo(() => {
    // When searching, show all search results (even if they're in recent)
    // When not searching, filter out recent orgs to avoid duplicates
    if (debouncedSearch) {
      return connectionOrganizations
    }
    const recentIds = new Set(recentOrganizations.map((org) => org.id))
    return connectionOrganizations.filter((org) => !recentIds.has(org.id))
  }, [connectionOrganizations, recentOrganizations, debouncedSearch])

  if (loading && !selectedOrganization) {
    return <Skeleton className="h-10 w-full" />
  }

  const activeOrganization = selectedOrganization ?? currentOrganization

  if (!activeOrganization) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <span className="text-xs font-semibold uppercase">
                  {activeOrganization.name.slice(0, 2)}
                </span>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{activeOrganization.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {formatOrganizationType(activeOrganization.organizationType)}
                </span>
              </div>
              {switching ? <Loader2 className="ml-auto size-4 animate-spin" /> : <ChevronsUpDown className="ml-auto size-4" />}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-64 rounded-lg p-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <Command className="border-none" shouldFilter={false}>
              <div className="px-3 py-2">
                <CommandInput
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                  placeholder="Search organizations..."
                  className="h-8"
                />
              </div>
              <CommandList>
                <CommandEmpty>
                  {debouncedSearch ? "No organizations match your search." : "No organizations available."}
                </CommandEmpty>
                {recentOrganizations.length > 0 && !debouncedSearch && (
                  <CommandGroup heading="Recent">
                    {recentOrganizations.map((org) => (
                      <CommandItem
                        key={org.id}
                        value={org.id}
                        onSelect={handleSelect}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{org.name}</span>
                        <Check
                          className={cn("size-4", org.id === activeOrganization?.id ? "opacity-100" : "opacity-0")}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {recentOrganizations.length > 0 && filteredOrganizations.length > 0 && !debouncedSearch && <CommandSeparator />}
                {filteredOrganizations.length > 0 && (
                  <CommandGroup heading={debouncedSearch ? "Search results" : undefined}>
                  {filteredOrganizations.map((org) => (
                    <CommandItem
                      key={org.id}
                      value={org.id}
                      onSelect={handleSelect}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{org.name}</span>
                      <Check
                        className={cn("size-4", org.id === activeOrganization?.id ? "opacity-100" : "opacity-0")}
                      />
                    </CommandItem>
                  ))}
                  {hasNextPage && (
                    <CommandItem
                      value="load-more"
                      onSelect={() => {
                        handleLoadMore()
                        setOpen(true)
                      }}
                      disabled={isSearching}
                      className="justify-center text-sm"
                    >
                      {isSearching ? <Loader2 className="size-4 animate-spin" /> : "Load more"}
                    </CommandItem>
                  )}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
